import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShiftAssignmentGrid = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [draggedShift, setDraggedShift] = useState(null);

  const shifts = [
    {
      id: 1,
      time: "06:00-14:00",
      department: "Customer Service",
      employee: "Anna Müller",
      employeeId: "EMP001",
      status: "assigned",
      priority: "normal"
    },
    {
      id: 2,
      time: "06:00-14:00",
      department: "Operations",
      employee: "Thomas Weber",
      employeeId: "EMP002",
      status: "assigned",
      priority: "high"
    },
    {
      id: 3,
      time: "06:00-14:00",
      department: "Manufacturing",
      employee: null,
      employeeId: null,
      status: "vacant",
      priority: "urgent"
    },
    {
      id: 4,
      time: "14:00-22:00",
      department: "Customer Service",
      employee: "Maria Schmidt",
      employeeId: "EMP003",
      status: "assigned",
      priority: "normal"
    },
    {
      id: 5,
      time: "14:00-22:00",
      department: "Operations",
      employee: "Klaus Fischer",
      employeeId: "EMP004",
      status: "pending",
      priority: "normal"
    },
    {
      id: 6,
      time: "14:00-22:00",
      department: "Manufacturing",
      employee: "Sarah Bauer",
      employeeId: "EMP005",
      status: "assigned",
      priority: "normal"
    },
    {
      id: 7,
      time: "22:00-06:00",
      department: "Customer Service",
      employee: null,
      employeeId: null,
      status: "vacant",
      priority: "high"
    },
    {
      id: 8,
      time: "22:00-06:00",
      department: "Operations",
      employee: "Michael König",
      employeeId: "EMP006",
      status: "assigned",
      priority: "normal"
    }
  ];

  const availableEmployees = [
    { id: "EMP007", name: "Lisa Wagner", department: "Customer Service", skills: ["Phone Support", "Chat"] },
    { id: "EMP008", name: "Peter Hoffmann", department: "Operations", skills: ["Logistics", "Quality Control"] },
    { id: "EMP009", name: "Julia Richter", department: "Manufacturing", skills: ["Assembly", "Quality Check"] },
    { id: "EMP010", name: "Stefan Braun", department: "Customer Service", skills: ["Email Support", "Technical"] }
  ];

  const getStatusColor = (status, priority) => {
    if (status === 'vacant') {
      return priority === 'urgent' ? 'bg-error/10 border-error text-error' :
             priority === 'high'? 'bg-warning/10 border-warning text-warning' : 'bg-muted border-border text-muted-foreground';
    }
    if (status === 'pending') {
      return 'bg-warning/10 border-warning text-warning';
    }
    return 'bg-success/10 border-success text-success';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'assigned':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'vacant':
        return 'AlertCircle';
      default:
        return 'Circle';
    }
  };

  const handleDragStart = (e, shift) => {
    setDraggedShift(shift);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetShift) => {
    e?.preventDefault();
    if (draggedShift && draggedShift?.id !== targetShift?.id) {
      console.log(`Reassigning shift ${draggedShift?.id} to ${targetShift?.id}`);
      // Handle shift reassignment logic here
    }
    setDraggedShift(null);
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate?.setDate(newDate?.getDate() + direction);
    setSelectedDate(newDate);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 h-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Calendar" size={18} className="text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Schichtzuteilung</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" onClick={() => navigateDate(-1)}>
            <Icon name="ChevronLeft" size={16} />
          </Button>
          <span className="text-sm font-medium text-foreground min-w-[200px] text-center">
            {formatDate(selectedDate)}
          </span>
          <Button variant="ghost" size="icon" onClick={() => navigateDate(1)}>
            <Icon name="ChevronRight" size={16} />
          </Button>
        </div>
      </div>
      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        {['06:00-14:00', '14:00-22:00', '22:00-06:00']?.map((timeSlot) => (
          <div key={timeSlot} className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-foreground">{timeSlot}</h4>
              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="xs" iconName="Plus" iconSize={12}>
                  Schicht hinzufügen
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {shifts?.filter(shift => shift?.time === timeSlot)?.map((shift) => (
                <div
                  key={shift?.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, shift)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, shift)}
                  className={`p-3 rounded-lg border cursor-move transition-all hover:shadow-card ${getStatusColor(shift?.status, shift?.priority)}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium uppercase tracking-wide">
                      {shift?.department}
                    </span>
                    <Icon name={getStatusIcon(shift?.status)} size={14} />
                  </div>

                  {shift?.employee ? (
                    <div>
                      <div className="font-medium text-sm">{shift?.employee}</div>
                      <div className="text-xs text-muted-foreground">{shift?.employeeId}</div>
                    </div>
                  ) : (
                    <div className="text-sm font-medium">
                      {shift?.priority === 'urgent' ? 'DRINGEND BESETZT' :
                       shift?.priority === 'high' ? 'Hohe Priorität' : 'Offen'}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-2">
                    <div className={`w-2 h-2 rounded-full ${
                      shift?.priority === 'urgent' ? 'bg-error' :
                      shift?.priority === 'high' ? 'bg-warning' : 'bg-muted-foreground'
                    }`} />
                    <div className="flex space-x-1">
                      <Button variant="ghost" size="icon" className="w-6 h-6">
                        <Icon name="Edit2" size={12} />
                      </Button>
                      <Button variant="ghost" size="icon" className="w-6 h-6">
                        <Icon name="MoreHorizontal" size={12} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Verfügbare Mitarbeiter: {availableEmployees?.length}
          </div>
          <div className="flex items-center space-x-4 text-xs">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-success rounded-full" />
              <span>Zugewiesen</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-warning rounded-full" />
              <span>Ausstehend</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-error rounded-full" />
              <span>Offen</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShiftAssignmentGrid;